import { useCallback } from 'react';

import { useLazyQuery } from '@apollo/client';

import { get as getUser } from 'js/lib/user';

import GetSkillAreaCourseItemsQuery, {
  type GetSkillAreaCourseItemsQueryData,
  type GetSkillAreaCourseItemsQueryVariables,
} from 'bundles/assess-common/api/GetSkillAreaCourseItems';
import GetCourseItemInfoQuery from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/GetCourseItemInfo.graphql';
import type {
  GetCourseItemInfoQuery as GetCourseItemInfoData,
  GetCourseItemInfoQueryVariables,
} from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetCourseItemInfo';

const VERIFIED_SKILLS_ASSESSMENT_FEATURE = 'VERIFIED_SKILLS_ASSESSMENT';

const useGetSkillPathwayCourseStartLink = (courseId: string, courseSlug: string) => {
  const userId = getUser().id;

  const [getSkillAreaCourseItems] = useLazyQuery<
    GetSkillAreaCourseItemsQueryData,
    GetSkillAreaCourseItemsQueryVariables
  >(GetSkillAreaCourseItemsQuery, {
    variables: {
      courseId: courseId || '',
      learnerId: `${userId}` || '',
    },
    fetchPolicy: 'cache-first',
  });

  const [getCourseItemInfo] = useLazyQuery<GetCourseItemInfoData, GetCourseItemInfoQueryVariables>(
    GetCourseItemInfoQuery,
    {
      context: { clientName: 'gatewayGql' },
      fetchPolicy: 'cache-first',
    }
  );

  // This returns the link to the first uncompleted item in the course or the course home link
  const fetchSkillPathwayCourseStartLink = useCallback<() => Promise<string>>(async () => {
    const courseHomeLink = `/learn/${courseSlug}/home/welcome`;

    try {
      const itemsInCourseResponse = await getSkillAreaCourseItems();
      const itemIdsInCourse =
        itemsInCourseResponse?.data?.OnDemandCourseMaterialsV2?.learner?.linked?.onDemandCourseMaterialItemsV2?.map(
          (item) => item.id
        ) || [];

      if (itemIdsInCourse.length === 0) {
        return courseHomeLink;
      }

      const itemsResponse = await getCourseItemInfo({
        variables: {
          courseId,
          itemIds: itemIdsInCourse,
          limit: itemIdsInCourse.length,
        },
      });

      // Create a map of itemId to index to preserve the order from itemIdsInCourse
      const itemIdToIndex = new Map<string, number>();
      itemIdsInCourse.forEach((itemId, index) => {
        if (itemId) {
          itemIdToIndex.set(itemId, index);
        }
      });

      const orderedItems =
        itemsResponse?.data?.NaptimeQueries?.OnDemandLearnerMaterialItemsV1Resource?.items?.elements
          ?.slice()
          .sort((itemA, itemB) => {
            const indexA = itemIdToIndex.get(itemA?.itemId || '') ?? Number.MAX_SAFE_INTEGER;
            const indexB = itemIdToIndex.get(itemB?.itemId || '') ?? Number.MAX_SAFE_INTEGER;
            return indexA - indexB;
          }) || [];

      const firstUncompletedItemInCourse = orderedItems.find((item) => !item?.isPassedOrCompleted);

      // Fallback to the first item if all items in the course are completed
      const firstItemInCourse = firstUncompletedItemInCourse || orderedItems[0];

      const itemTypename = firstItemInCourse?.contentSummary?.__typename;

      if (!itemTypename || !firstItemInCourse) {
        return courseHomeLink;
      }

      const itemTypeSlug = itemTypename.replace('OnDemandLearnerMaterialItemsV1_', '').replace('Member', '');

      return `/learn/${courseSlug}/${itemTypeSlug}/${firstItemInCourse.itemId}`;
    } catch {
      return courseHomeLink;
    }
  }, [courseId, courseSlug, getCourseItemInfo, getSkillAreaCourseItems]);

  // This returns the itemId of the first incomplete skill assessment in the course,
  // or the last skill assessment if all are completed and passed, or undefined if none found
  const fetchSkillPathwayCourseFirstIncompleteAssessment = useCallback<() => Promise<string | undefined>>(async () => {
    try {
      const itemsInCourseResponse = await getSkillAreaCourseItems();
      const skillAssessmentItemsInCourse =
        itemsInCourseResponse?.data?.OnDemandCourseMaterialsV2?.learner?.linked?.onDemandCourseMaterialItemsV2?.filter(
          (item) =>
            item.contentSummary?.typeName === 'staffGraded' &&
            item.contentSummary?.definition?.assignmentFeatures?.includes(VERIFIED_SKILLS_ASSESSMENT_FEATURE)
        );
      const skillAssessmentItemIdsInCourse = skillAssessmentItemsInCourse?.map((item) => item.id) || [];

      if (skillAssessmentItemIdsInCourse.length === 0) {
        return undefined;
      }

      const skillAssessmentItemsResponse = await getCourseItemInfo({
        variables: {
          courseId,
          itemIds: skillAssessmentItemIdsInCourse,
          limit: skillAssessmentItemIdsInCourse.length, // this query caps responses at 100 by default
        },
      });

      // Create a map of itemId to index to preserve the order from itemIdsInCourse
      const skillAssessmentItemIdToIndex = new Map<string, number>();
      skillAssessmentItemIdsInCourse.forEach((itemId, index) => {
        if (itemId) {
          skillAssessmentItemIdToIndex.set(itemId, index);
        }
      });

      const orderedSkillAssessmentItems =
        skillAssessmentItemsResponse?.data?.NaptimeQueries?.OnDemandLearnerMaterialItemsV1Resource?.items?.elements
          ?.slice()
          .sort((itemA, itemB) => {
            const indexA = skillAssessmentItemIdToIndex.get(itemA?.itemId || '') ?? Number.MAX_SAFE_INTEGER;
            const indexB = skillAssessmentItemIdToIndex.get(itemB?.itemId || '') ?? Number.MAX_SAFE_INTEGER;
            return indexA - indexB;
          }) || [];

      // Filter for verified skill assessments by checking assignmentFeatures from the staffGraded content
      const skillAssessmentItems = orderedSkillAssessmentItems.map((item) => ({
        itemId: item!.itemId,
        isPassedOrCompleted: item?.isPassedOrCompleted ?? false,
      }));

      if (skillAssessmentItems.length === 0) {
        return undefined;
      }

      // Find first incomplete assessment, or fall back to last assessment if all are completed and passed
      const firstIncompleteAssessment = skillAssessmentItems.find((item) => !item.isPassedOrCompleted);
      return firstIncompleteAssessment?.itemId ?? skillAssessmentItems[skillAssessmentItems.length - 1]?.itemId;
    } catch {
      return undefined;
    }
  }, [courseId, getCourseItemInfo, getSkillAreaCourseItems]);

  return { fetchSkillPathwayCourseStartLink, fetchSkillPathwayCourseFirstIncompleteAssessment };
};

export default useGetSkillPathwayCourseStartLink;
